import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../utils/requestMethods";
import { exportToCSV } from "../utils/exportCSV";
import { FiMoreHorizontal, FiDownload, FiUsers, FiLayers } from "react-icons/fi";
import {
  Header,
  CustomPagination,
  BackButton,
  SkeletonTable,
  AddSubjectsDetailsModal,
  EditSubject,
  TableDropdown,
  ConfirmationModal,
} from "../components";
import SubjectDetailModal from "../components/SubjectDetailModal";
import { useStateContext } from "../contexts/ContextProvider";
import * as Icons from "../data/class";
import { getArmLabel } from "../utils/classGroupHelpers";
import { MdSearch, MdVisibility } from "react-icons/md";

const customStyles = {
  headRow: {
    style: {
      backgroundColor: "rgb(248, 250, 252)",
      fontWeight: "bold",
    },
  },
};

const Subjects = ({ history }) => {
  const location = useLocation();
  const classId = location.pathname.split("/")[2];
  //console.log(classId)
  const { school } = useStateContext();
  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term;
  //const schoolSession = school?.settings?.current_session;
  const [subjects, setSubjects] = useState([]);
  const [classInfo, setClassInfo] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSubjectDetailModalOpen, setIsSubjectDetailModalOpen] = useState(false);
  const [currentsubject, setCurrentSubject] = useState([]);
  const [selectedSubjectForDetail, setSelectedSubjectForDetail] = useState(null);
  //const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [filterText, setFilterText] = useState("");

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const openDeleteModal = (subject) => {
    setCurrentSubject(subject);
    setIsDeleteModalOpen(true);
  };

  const toggleSubjectDetailModal = (subject = null) => {
    setSelectedSubjectForDetail(subject);
    setIsSubjectDetailModalOpen(!isSubjectDetailModalOpen);
  };

  useEffect(() => {
    if (classId && schoolId) {
      fetchSubjects();
      fetchClass();
    }
  }, [classId, schoolId]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.get(`subjects/${schoolId}/${classId}?limit=200`);
      //console.log(data);
      setSubjects(data.subjects);
      setLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const fetchClass = async () => {
    try {
      //setLoading(true);
      const { data } = await userRequest.get(`classes/${schoolId}/${classId}`);
      //console.log(data);
      setClassInfo(data);
      //setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await userRequest.delete(`/subjects/${schoolId}/${classId}/${currentsubject._id}`);
      setSubjects(subjects.filter((subject) => subject._id !== currentsubject._id));
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDropdownClick = (rowIndex, event) => {
    const rect = event.target.getBoundingClientRect();
    setDropdownIndex(dropdownIndex === rowIndex ? null : rowIndex);
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleCloseDropdown = () => setDropdownIndex(null);

  const TeachersCell = ({ row }) => {
    const teachers = row.subject_teachers || [];
    const teacherCount = teachers.length;

    if (teacherCount === 0) {
      return (
        <div className="flex items-center gap-2">
          <FiUsers className="text-gray-400 text-sm" />
          <span className="text-sm text-gray-500 font-semibold">0</span>
        </div>
      );
    }

    const primaryCount = teachers.filter((t) => t.role === "primary").length;
    const assistantCount = teachers.filter((t) => t.role === "assistant").length;

    return (
      <div className="flex items-center gap-2">
        <FiUsers className="text-indigo-600 text-sm" />
        <span className="text-sm font-semibold text-gray-700">{teacherCount}</span>
        {(primaryCount > 0 || assistantCount > 0) && (
          <div className="flex items-center gap-1 ml-1">
            {primaryCount > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {primaryCount}P
              </span>
            )}
            {assistantCount > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                {assistantCount}A
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleExport = () => {
    const exportColumns = [
      {
        name: "Subject Name",
        exportCell: (row) => row.subject_name || "",
      },
      {
        name: "Subject Description",
        exportCell: (row) => row.subject_description || "",
      },
      {
        name: "Pass Mark",
        exportCell: (row) => row.pass_mark || 0,
      },
    ];

    const className = classInfo?.class_name || "Class";
    const filename = `${className}_subjects_${new Date().toISOString().split("T")[0]}.csv`;

    exportToCSV(filteredData, exportColumns, filename);
  };

  const columns = [
    {
      name: "Subject Name",
      selector: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-semibold">{row.subject_name}</span>
          {row.subject_ref_id && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
              Shared
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Description",
      selector: (row) => row.subject_description,
    },
    /*{
      name: "Subject Code",
      selector: row => row.subject_code
    },*/
    {
      name: "Pass Mark",
      selector: (row) => `${row.pass_mark || 50}%`,
      cell: (row) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          {row.pass_mark || 0}%
        </span>
      ),
    },
    {
      name: "Lesson Notes",
      selector: (row) => {
        const termNotes = row.lesson_note.find((ln) => ln.term === schoolTerm);
        return termNotes ? termNotes.notes.length : 0;
      },
    },
    {
      name: "Teachers",
      selector: (row) => <TeachersCell row={row} />,
      width: "150px",
    },
    /*{
      name: "Date Created",
      selector: row => (new Date(row.created_at)).toLocaleDateString('en-NG')
    },*/
    {
      cell: (row, rowIndex) => (
        <div className="relative">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={(e) => handleDropdownClick(rowIndex, e)}
          >
            <FiMoreHorizontal className="text-2xl" />
          </button>
          <TableDropdown
            isVisible={dropdownIndex === rowIndex}
            position={dropdownPosition}
            options={[
              {
                label: "Subject Teachers",
                icon: <MdVisibility className="text-2xl text-slate-400" />,
                onClick: () => {
                  handleCloseDropdown();
                  toggleSubjectDetailModal(row);
                },
              },
              {
                label: "View Lesson Notes",
                icon: <img src={Icons.checkList} alt="Payout Icon" />,
                onClick: () => {
                  history.push(`/notes/${row._id}/${classId}`, { subject: row });
                },
              },
              {
                label: "Edit Subject",
                icon: <img src={Icons.pencilEdit} alt="Payout Icon" />,
                onClick: () => {
                  toggleEditModal();
                  setCurrentSubject(row);
                  handleCloseDropdown();
                },
              },
              {
                label: "Delete",
                icon: <img src={Icons.delete} alt="Payout Icon" />,
                onClick: () => {
                  openDeleteModal(row);
                  handleCloseDropdown();
                },
              },
            ]}
            onClose={handleCloseDropdown}
          />
        </div>
      ),
      button: true,
      //width: "120px",
    },
  ];

  const filteredData = subjects?.filter(
    (item) =>
      item.subject_name?.toLowerCase().includes(filterText.toLowerCase()) ||
      "" ||
      item.subject_description?.toLowerCase().includes(filterText.toLowerCase()) ||
      "",
  );

  return (
    <>
      <div className="px-10 pt-6">
        <BackButton history={history} />
      </div>
      <div className="px-10 pt-5">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <Header
            category="Subjects"
            title={classInfo?.class_name ? `${classInfo.class_name} Subjects` : "Loading..."}
          />

          <div className="flex flex-wrap justify-center md:justify-end w-full md:w-auto">
            <button
              type="button"
              onClick={toggleModal}
              className="inline-flex justify-center py-2 px-4 mb-2 md:mb-0 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-[#3F3DA5] hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2"
            >
              Add Subjects
            </button>
          </div>
        </div>
      </div>

      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg">
        {school?.settings?.class_grouping_enabled && classInfo?.class_group_id && (
          <div className="mb-6 p-4 rounded-2xl bg-indigo-50 border border-indigo-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 text-indigo-700 rounded-xl">
                <FiLayers className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-indigo-900">
                  Part of Class Group{" "}
                  {classInfo.group_label
                    ? `(${getArmLabel(school)} ${classInfo.group_label})`
                    : ""}
                </p>
                <p className="text-xs text-indigo-700">
                  Shared subjects and default teachers can also be managed for all arms
                  simultaneously.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => history.push(`/class-groups/${classInfo.class_group_id}`)}
              className="px-3.5 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex-shrink-0"
            >
              Manage Class Group →
            </button>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h1 className="text-lg font-semibold w-44">Subject Details</h1>
          <div className="flex justify-center md:justify-end w-full gap-2">
            <form className="max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 start-0 flex items-center pl-4 pointer-events-none">
                  <MdSearch />
                </div>
                <input
                  type="text"
                  id="email-address-icon"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-md rounded-full focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
                  placeholder="Search"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </form>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title="Export to CSV"
            >
              <FiDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="border rounded-3xl overflow-x-auto w-full max-w-screen-lg mx-auto">
          <DataTable
            data={filteredData}
            columns={columns}
            getRowId={(row) => row._id}
            pagination
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
          />
        </div>
        {isModalOpen && (
          <AddSubjectsDetailsModal
            closeModal={toggleModal}
            onSubjectCreated={fetchSubjects}
            schoolId={schoolId}
            classId={classId}
          />
        )}

        {isEditModalOpen && (
          <EditSubject
            closeModal={toggleEditModal}
            onSubjectUpdated={fetchSubjects}
            currentsubject={currentsubject}
            schoolId={schoolId}
            classId={classId}
          />
        )}

        <ConfirmationModal
          isVisible={isDeleteModalOpen}
          title="Delete Subject"
          description={`Are you sure you want to delete the subject "${currentsubject?.subject_name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setIsDeleteModalOpen(false)}
          deleting={deleting}
        />

        {/* Subject Detail Modal */}
        {isSubjectDetailModalOpen && selectedSubjectForDetail && (
          <SubjectDetailModal
            isOpen={isSubjectDetailModalOpen}
            onClose={() => toggleSubjectDetailModal()}
            subjectData={selectedSubjectForDetail}
            classId={classId}
            schoolId={schoolId}
          />
        )}
      </div>
    </>
  );
};
export default Subjects;
